import React, { useState } from 'react';
import { Upload, Button, message, Alert } from 'antd';
import { UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';

const FileUpload = ({ onFileUpload, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const uploadToBackend = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);

      // Get auth token from user_customer_details in localStorage
      const userStorage = localStorage.getItem('user_customer_details');
      const user = userStorage ? JSON.parse(userStorage) : null;

      if (!user || !user.token) {
        console.error('Missing user token:', {
          userExists: !!user,
          tokenExists: !!(user?.token)
        });
        throw new Error('Authentication required. Please sign in.');
      }

      const uploadUrl = `${process.env.REACT_APP_MULTI_LEVEL_BASE_URL}/auth/upload_payment_receipt`;
      console.log('FileUpload - Uploading to:', uploadUrl);

      const response = await axios.post(
        uploadUrl,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            // Don't set Content-Type for FormData, let browser set it with boundary
          }
        }
      );

      console.log('FileUpload - Response:', response.data);

      // Check for success - backend returns { status: "success", data: { url, signedUrl, key } }
      const resData = response.data;
      const isSuccess = response.status === 200 && resData && (
        resData.status === 'success' || 
        resData.success === true
      );

      if (isSuccess) {
        message.success('Receipt uploaded successfully!');
        
        // Extract URL from nested data structure: response.data.data.url
        const fileUrl = resData.data?.signedUrl || resData.data?.url || resData.url || resData.file_url;
        
        const callbackData = {
          url: fileUrl,
          name: file.name,
          size: file.size,
          type: file.type,
          response: resData
        };
        
        console.log('FileUpload: Upload successful, URL:', fileUrl);
        
        if (onFileUpload) {
          onFileUpload(callbackData);
        }

        setFileList([{
          uid: file.uid,
          name: file.name,
          status: 'done',
          url: fileUrl,
          response: resData
        }]);

        return resData;
      } else {
        throw new Error(resData?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Upload error response:', error.response?.status, error.response?.data);
      
      let errorMessage = 'Upload failed. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);

      // Update file list with error status
      setFileList([{
        uid: file.uid,
        name: file.name,
        status: 'error',
        response: error.response?.data || { message: errorMessage }
      }]);

      throw error;
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      message.error('Please upload only PDF, JPG, JPEG, or PNG files.');
      return false;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      message.error('File size must be less than 5MB.');
      return false;
    }

    return true;
  };

  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      const result = await uploadToBackend(file);
      onSuccess(result, file);
    } catch (error) {
      onError(error);
    }
  };

  const handleChange = (info) => {
    setFileList(info.fileList);

    if (info.file.status === 'uploading') {
      return;
    }

    if (info.file.status === 'done') {
      message.success(`${info.file.name} uploaded successfully`);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} upload failed.`);
    }
  };

  const handleRemove = (file) => {
    setFileList([]);
    if (onFileUpload) {
      onFileUpload(null); // Clear the uploaded file in parent component
    }
  };

  const uploadProps = {
    name: 'receipt',
    multiple: false,
    maxCount: 1,
    fileList,
    beforeUpload,
    customRequest,
    onChange: handleChange,
    onRemove: handleRemove,
    accept: '.pdf,.jpg,.jpeg,.png',
    disabled: disabled || uploading
  };

  return (
    <div className="file-upload-container">
      <Alert
        message="Upload Receipt"
        description="Upload a clear image or PDF of your bank transfer receipt"
        type="info"
        showIcon
        style={{ 
          marginBottom: 16,
          backgroundColor: '#e6f7ff',
          border: '1px solid #91d5ff',
          borderRadius: '4px'
        }}
        styles={{
          message: { 
            color: '#0050b3',
            fontWeight: 600,
            fontSize: '14px'
          },
          description: { 
            color: '#096dd9',
            fontSize: '13px'
          }
        }}
      />
      
      <Upload.Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          {uploading ? <LoadingOutlined /> : <UploadOutlined />}
        </p>
        <p className="ant-upload-text">
          {uploading ? 'Uploading...' : 'Click or drag file to upload'}
        </p>
        <p className="ant-upload-hint">
          PDF, JPG, JPEG, PNG • Max 5MB
        </p>
      </Upload.Dragger>

      {fileList.length > 0 && fileList[0].status === 'done' && (
        <Alert
          message="Upload Successful"
          description="Receipt uploaded successfully. You can now proceed."
          type="success"
          showIcon
          size="small"
          style={{ marginTop: 16 }}
        />
      )}

      {fileList.length > 0 && fileList[0].status === 'error' && (
        <Alert
          message="Upload Failed"
          description="There was an error uploading your receipt. Please try again."
          type="error"
          showIcon
          size="small"
          style={{ marginTop: 16 }}
        />
      )}
    </div>
  );
};

export default FileUpload;
